import { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link, useParams } from 'react-router'
import { rpc, useRpcEvent } from '../api/rpc'
import type { ChatTranscriptItem, Routine, RoutineRun } from '../api/types'
import { MessageItem } from '../components/chat/MessageItem'
import { StepGroup } from '../components/chat/StepGroup'
import { groupFeed } from '../components/chat/feed'
import { RunStatusBadge } from '../components/RunStatusBadge'
import { Button } from '../components/ui/Button'
import { Card, InfoRow, PageHeader } from '../components/ui/Card'
import { formatDateTime, formatUsd } from '../lib/format'

/**
 * One run: the numbers on a single line under the title, then the transcript (unchanged chat feed) on
 * the left with the run's metadata pinned on the right. The transcript is the reason the page exists,
 * so it gets the wide column and the metadata never sits between it and the top of the page.
 */
export function RunPage() {
  const { t, i18n } = useTranslation()
  const { runId = '' } = useParams()
  const [data, setData] = useState<{ run: RoutineRun; routine: Routine; transcript: { items: ChatTranscriptItem[] } } | null>(null)
  const load = useCallback(() => rpc.call<typeof data>('routines.run', { runId }).then(setData), [runId])
  useEffect(() => { void load() }, [load])
  useRpcEvent('routine.run', (r: RoutineRun) => { if (r.id === runId) void load() })
  if (!data) return null
  const { run, routine } = data
  const active = ['queued', 'running', 'waiting-approval'].includes(run.status)
  const dur = run.startedAt && run.finishedAt ? `${((run.finishedAt - run.startedAt) / 1000).toFixed(1)} s` : '—'
  const tokens = run.inputTokens === null && run.outputTokens === null ? '—' : `${run.inputTokens ?? 0} / ${run.outputTokens ?? 0}`
  return (
    <div className="mx-auto w-full max-w-[1180px] px-6 pb-12 pt-9 max-sm:px-4 max-sm:pt-6">
      <PageHeader
        title={<span className="flex items-center gap-3">{routine.name}<RunStatusBadge status={run.status} /></span>}
        subtitle={<Link to={`/automations/${routine.id}`} className="hover:text-ink-100 hover:underline">← {t('automations.backToRoutine')}</Link>}
        actions={active && <Button variant="secondary" onClick={() => void rpc.call('routines.cancelRun', { runId })}>{t('automations.cancelRun')}</Button>}
      />
      <div className="-mt-2 flex flex-wrap items-center gap-x-5 gap-y-1.5 text-[13px] text-ink-300 [&>span+span]:border-l [&>span+span]:border-line [&>span+span]:pl-5">
        <span>{t('automations.run.trigger')} <b className="font-semibold text-ink-100">{t(`automations.trigger.${run.trigger.kind}`)}</b></span>
        <span className="tabular-nums">{formatDateTime(run.queuedAt, i18n.language)}</span>
        <span>{t('automations.run.duration')} <b className="font-semibold text-ink-100 tabular-nums">{dur}</b></span>
        <span className="tabular-nums">{formatUsd(run.costUsd)}</span>
        {run.path && <span className="font-mono text-[12px] text-ink-500">{t(`automations.path_${run.path}`)}</span>}
      </div>
      {run.endReason && run.status !== 'succeeded' && (
        <p className="mt-4 rounded-xl border border-red-300 bg-red-50 px-4 py-2.5 text-[13px] text-red-800" role="alert">{run.error ?? run.endReason}</p>
      )}
      <div className="mt-6 grid items-start gap-5 min-[1000px]:grid-cols-[minmax(0,1fr)_300px]">
        <div className="min-w-0 space-y-4">
          {groupFeed(data.transcript.items).map(block => block.kind === 'message'
            ? <MessageItem key={block.message.id} message={block.message} streaming={false} />
            : <StepGroup key={block.id} steps={block.steps} />)}
        </div>
        <aside className="min-w-0 space-y-5 min-[1000px]:sticky min-[1000px]:top-4">
          <Card title={t('automations.run.info')}>
            <dl className="space-y-2.5">
              <InfoRow label={t('automations.run.status')} value={<RunStatusBadge status={run.status} />} />
              <InfoRow label={t('automations.run.ended')} value={run.endReason ?? '—'} />
              <InfoRow label={t('automations.run.path')} value={run.path ? t(`automations.path_${run.path}`) : '—'} />
              <InfoRow label={t('automations.run.tokens')} value={<span className="font-mono tabular-nums">{tokens}</span>} />
              <InfoRow label={t('usage.cost')} value={<span className="font-mono tabular-nums">{formatUsd(run.costUsd)}</span>} />
              <InfoRow label={t('automations.run.session')} value={run.sessionId ? <span className="font-mono text-[11.5px]">{run.sessionId.slice(0, 8)}</span> : t('automations.run.noSession')} />
              {run.attempt > 1 && <InfoRow label={t('automations.run.attempt')} value={<span className="font-mono tabular-nums">{run.attempt}</span>} />}
              <InfoRow label={t('automations.run.id')} value={<span className="font-mono text-[11.5px]">{run.id.slice(0, 8)}</span>} />
            </dl>
          </Card>
          {run.trigger.event !== undefined && (
            <Card title={t('automations.run.event')}>
              <pre className="code-block max-h-64 overflow-auto rounded-lg p-2.5 font-mono text-xs">{JSON.stringify(run.trigger.event, null, 2)}</pre>
            </Card>
          )}
        </aside>
      </div>
    </div>
  )
}
